import { Box, HStack, Tooltip, keyframes } from '@chakra-ui/react';
import { Button } from 'components/v4';
import { useLayoutEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { IoWarning } from 'react-icons/io5';
import { FaFileUpload, FaBolt, FaLayerGroup } from 'react-icons/fa';
import ScopeType from 'utils/enums/ScopeType';
import { PlatformEnums } from 'utils/platforms';
import numberPath from '@/routes/numbers/numberPath';
import PlatformFilterStack from '../../../../../components/tables/AGGrid/PlatformFilterStack';
import {
    PLATFORM_STACK_MIN_WIDTH,
    getPlatformStackFullWidth,
} from '../../../../../components/tables/AGGrid/platformFilterStackSizing';
import CollapsibleButtonGroup from '../../../../../components/presentational/CollapsibleButtonGroup';
import { getButtonGroupFullWidth } from '../../../../../components/presentational/collapsibleButtonGroupSizing';
import { AddNumbers } from 'pages/telephony/numbers/edit/AddNumbers';

const ROW_GAP = 8;
const TOOLBAR_PADDING_LEFT = 20;
// Room inside the clipped row for the urgent glow (its pulse spreads 10px).
// It's part of the toolbar's left padding, so nothing moves.
const GLOW_SPACE = 10;
// The Confirm button's fixed width, and Add Numbers' and the collapsed
// Number Actions menu button's until they're measured
const CONFIRM_WIDTH = 110;
const ADD_NUMBERS_ESTIMATED_WIDTH = 140;
const NUMBER_ACTIONS_ESTIMATED_WIDTH = 140;

// Define pulsate animation
const pulsate = keyframes`
  0% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(255, 69, 58, 0.7);
  }
  70% {
    transform: scale(1.05);
    box-shadow: 0 0 0 10px rgba(255, 69, 58, 0);
  }
  100% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(255, 69, 58, 0);
  }
`;

// The Confirm button's pulse and shimmer. The collapsed Number Actions button
// uses it too while Confirm is folded inside it.
const URGENT_BUTTON_SX = {
    animation: `${pulsate} 2s infinite`,
    position: 'relative',
    overflow: 'hidden',
    '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: '-100%',
        width: '100%',
        height: '100%',
        background:
            'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)',
        animation: 'shimmer 2.5s infinite',
    },
    '@keyframes shimmer': {
        '0%': { left: '-100%' },
        '100%': { left: '100%' },
    },
    // Hover effects
    '&:hover': {
        transform: 'scale(1.08)',
        transition: 'transform 0.2s ease',
        animation: `${pulsate} 1.5s infinite`,
    },
    // Focus effects
    '&:focus': {
        animation: `${pulsate} 1s infinite`,
    },
    // Additional emphasis for urgency
    fontWeight: 'bold',
    letterSpacing: '0.5px',
};

// Confirm inside the collapsed menu: red and bold so it still reads as urgent.
// The hover and focus rules outrank v4 MenuList's grey item highlight.
const getUrgentMenuItemProps = (darkMode) => {
    const bg = darkMode ? 'rgba(254, 178, 178, 0.16)' : 'red.50';
    const activeBg = darkMode ? 'rgba(254, 178, 178, 0.28)' : 'red.100';
    return {
        color: darkMode ? 'red.300' : 'red.600',
        fontWeight: 'bold',
        bg,
        sx: {
            '&[role="menuitem"]:hover, &[role="menuitem"]:focus': {
                bg: activeBg,
            },
        },
    };
};

const NumberTableToolbar = ({
    currentScope,
    selectedTableData,
    tooltipShow,
    setTooltipShow,
    handleConfirm,
    navigate,
    menuData,
    platformList,
    selectedOption,
    setSelectedOption,
    selectedRows,
    handleSelectedActionNumber,
    isConfirming,
}) => {
    const { darkMode } = useSelector((state) => state.settings);
    const hasConfirmNumbers = selectedTableData?.data?.some(
        (row) => row.platformStatus === 17,
    );

    const handlePlatformSelect = (platform) => {
        if (selectedOption?.id === platform.id) {
            setSelectedOption(undefined);
            return;
        }
        setSelectedOption(platform);
    };

    const activePlatformId =
        platformList?.length === 1 ? platformList[0]?.id : selectedOption?.id;

    const toolbarItems = [
        currentScope === ScopeType.COMPANY && {
            key: 'csv-update',
            label: 'CSV Update',
            icon: FaFileUpload,
            onClick: () => navigate('/numbers/csv-numbers'),
            walkthroughId: '/numbers/button/csv-update',
        },
        currentScope === ScopeType.COMPANY &&
            activePlatformId === PlatformEnums.TEAMS && {
                key: 'bulk-operations',
                label: 'Bulk Update',
                icon: FaLayerGroup,
                onClick: () => navigate(numberPath.NUMBERS_BULK_TEAMS),
                walkthroughId: '/numbers/button/bulk-operations',
            },
        selectedRows.length > 0 && {
            key: 'action',
            label: isConfirming ? 'Actioning...' : 'Action',
            ariaLabel: 'action-number',
            icon: FaBolt,
            variant: 'outline',
            onClick: () => handleSelectedActionNumber(),
            walkthroughId: '/numbers/button/action-number',
        },
    ];
    const buttonCount = toolbarItems.filter(Boolean).length;

    // Collapsed, every action folds into one menu like the Users toolbar:
    // Confirm (first, in red), the buttons above, and the Add Numbers options.
    const collapsedItems = [
        hasConfirmNumbers && {
            key: 'confirm',
            label: 'Confirm',
            icon: IoWarning,
            onClick: handleConfirm,
            walkthroughId: '/numbers/button/confirm',
            menuItemProps: getUrgentMenuItemProps(darkMode),
        },
        ...toolbarItems,
        ...(menuData ?? []).map((item) => ({
            key: `add-numbers-${item.title}`,
            label: item.title,
            icon: item.icon,
            onClick: item.action,
        })),
    ];
    const hasPlatformFilter =
        (currentScope === ScopeType.COMPANY ||
            currentScope === ScopeType.PARTNER) &&
        platformList?.length > 1;

    // The actions and the platform filter stack share this one row, but each
    // independently deciding "do I have enough room" off its own
    // (non-growing) box can't detect the row getting wider again — growing
    // to measure correctly would just recreate the visual gap this fixes.
    // Instead, measure the row's real available width once here and hand
    // both a plain forceOverflowing boolean (same approach as the Users
    // toolbar).
    const rowRef = useRef(null);
    const addNumbersRef = useRef(null);
    // Add Numbers isn't rendered while collapsed, so keep its last width
    const addNumbersWidthRef = useRef(ADD_NUMBERS_ESTIMATED_WIDTH);
    const [buttonsOverflowing, setButtonsOverflowing] = useState(false);
    const [platformsOverflowing, setPlatformsOverflowing] = useState(false);
    // (3287) Collapsed, the toolbar keeps room for the whole Number Actions
    // button (and the platform stack), so the search box can't cut it off.
    const numberActionsRef = useRef(null);
    const [numberActionsWidth, setNumberActionsWidth] = useState(
        NUMBER_ACTIONS_ESTIMATED_WIDTH,
    );
    useLayoutEffect(() => {
        if (buttonsOverflowing && numberActionsRef.current) {
            setNumberActionsWidth(numberActionsRef.current.offsetWidth);
        }
        // Confirm's bold label makes the button wider
    }, [buttonsOverflowing, hasConfirmNumbers]);
    const collapsedMinWidth =
        TOOLBAR_PADDING_LEFT +
        numberActionsWidth +
        (hasPlatformFilter ? ROW_GAP + PLATFORM_STACK_MIN_WIDTH : 0);

    useLayoutEffect(() => {
        const row = rowRef.current;
        if (!row) return undefined;

        const checkWidth = () => {
            const availableForFlexZone = row.clientWidth - GLOW_SPACE;
            if (availableForFlexZone <= 0) return;

            if (addNumbersRef.current) {
                addNumbersWidthRef.current =
                    addNumbersRef.current.getBoundingClientRect().width;
            }
            const actionWidths = [
                buttonCount ? getButtonGroupFullWidth(buttonCount) : 0,
                hasConfirmNumbers ? CONFIRM_WIDTH : 0,
                addNumbersWidthRef.current,
            ].filter(Boolean);
            const buttonsFullWidth =
                actionWidths.reduce((total, width) => total + width, 0) +
                (actionWidths.length - 1) * ROW_GAP;
            const platformsFullWidth = hasPlatformFilter
                ? getPlatformStackFullWidth(platformList.length)
                : 0;
            const platformsCollapsedWidth = hasPlatformFilter
                ? PLATFORM_STACK_MIN_WIDTH
                : 0;
            const betweenGap = hasPlatformFilter ? ROW_GAP : 0;

            // Priority: the platform filters collapse first; the button
            // group only collapses once buttons-full + platforms-collapsed
            // no longer fits either.
            const bothFullWidth =
                buttonsFullWidth + betweenGap + platformsFullWidth;
            const buttonsFullPlatformsCollapsedWidth =
                buttonsFullWidth + betweenGap + platformsCollapsedWidth;

            if (availableForFlexZone >= bothFullWidth) {
                setButtonsOverflowing(false);
                setPlatformsOverflowing(false);
            } else if (
                availableForFlexZone >= buttonsFullPlatformsCollapsedWidth
            ) {
                setButtonsOverflowing(false);
                setPlatformsOverflowing(true);
            } else {
                setButtonsOverflowing(true);
                setPlatformsOverflowing(true);
            }
        };

        checkWidth();

        const resizeObserver = new ResizeObserver(checkWidth);
        resizeObserver.observe(row);
        return () => resizeObserver.disconnect();
    }, [
        buttonCount,
        hasConfirmNumbers,
        hasPlatformFilter,
        platformList?.length,
    ]);

    return (
        <div
            style={{
                paddingLeft: `${TOOLBAR_PADDING_LEFT - GLOW_SPACE}px`,
                overflow: 'hidden',
                minWidth: buttonsOverflowing ? collapsedMinWidth : 0,
                flex: 1,
            }}>
            <HStack
                spacing={2}
                ref={rowRef}
                overflow="hidden"
                minWidth={0}
                pl={`${GLOW_SPACE}px`}
                py={`${GLOW_SPACE}px`}>
                {buttonsOverflowing ? (
                    <Box ref={numberActionsRef} flexShrink={0}>
                        <CollapsibleButtonGroup
                            items={collapsedItems}
                            menuLabel="Number Actions"
                            forceOverflowing
                            // Let the Confirm glow show around the button
                            overflow="visible"
                            menuButtonProps={
                                hasConfirmNumbers
                                    ? {
                                          sx: URGENT_BUTTON_SX,
                                          'aria-label':
                                              'Number Actions: numbers need confirming',
                                      }
                                    : undefined
                            }
                        />
                    </Box>
                ) : (
                    <>
                        <CollapsibleButtonGroup
                            items={toolbarItems}
                            menuLabel="Number Actions"
                            forceOverflowing={false}
                        />

                        {/* Animated Confirm Button */}
                        {hasConfirmNumbers && (
                            <div
                                style={{
                                    position: 'relative',
                                    display: 'inline-block',
                                    flexShrink: 0,
                                }}>
                                <Button
                                    size="sm"
                                    width="110px"
                                    colorScheme="red"
                                    onClick={handleConfirm}
                                    data-walkthroughid="/numbers/button/confirm"
                                    sx={URGENT_BUTTON_SX}>
                                    <IoWarning
                                        style={{
                                            marginRight: '4px',
                                            animation:
                                                'bounce 1s infinite alternate',
                                        }}
                                    />
                                    Confirm
                                </Button>
                            </div>
                        )}

                        {/* Add Numbers Button */}
                        <Tooltip
                            label="Add Numbers"
                            placement="bottom"
                            hasArrow
                            portalProps={{ containerRef: undefined }}
                            isOpen={tooltipShow}
                            shouldWrapChildren>
                            <div
                                ref={addNumbersRef}
                                style={{
                                    position: 'relative',
                                    display: 'inline-block',
                                    flexShrink: 0,
                                }}
                                onMouseEnter={setTooltipShow.on}
                                onMouseLeave={setTooltipShow.off}>
                                <AddNumbers
                                    menuData={menuData}
                                    setTooltipShow={setTooltipShow}
                                />
                            </div>
                        </Tooltip>
                    </>
                )}

                {/* Platform Selection Buttons */}
                {hasPlatformFilter && (
                    <PlatformFilterStack
                        platformList={platformList}
                        isSelected={(platform) =>
                            selectedOption?.id === platform.id
                        }
                        onSelect={handlePlatformSelect}
                        forceOverflowing={platformsOverflowing}
                    />
                )}
            </HStack>
        </div>
    );
};

export default NumberTableToolbar;
